import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PostCard, MarketCard, UserChip } from "@/components/Social";
import { ManageListings, TradeHint } from "@/components/Trade";
import { BadgeStrip } from "@/components/Badges";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Loader2, Sparkles, Store, Tag } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { handle } = useParams();
  const { refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "looks";
  const setTab = (v) => setParams(v === "looks" ? {} : { tab: v });
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = () => { api.getUser(handle).then(setP).catch((e) => setErr(e?.response?.data?.detail || "Not found")); };
  useEffect(() => { setP(null); setErr(null); load(); }, [handle]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFollow = async () => {
    try { if (p.is_following) await api.unfollow(p.handle); else await api.follow(p.handle); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  if (err) return <div className="text-center text-muted-foreground py-20">{err}</div>;
  if (!p) return <div className="space-y-4"><Skeleton className="h-24 rounded-[var(--radius)]" /><Skeleton className="h-72 rounded-[var(--radius)]" /></div>;

  return (
    <div data-testid="profile-page">
      <div className="rounded-2xl border border-border bg-card p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <UserChip user={p} link={false} />
          {p.bio && <p className="text-sm mt-3 max-w-xl" data-testid="profile-bio">{p.bio}</p>}
          <p className="text-xs text-muted-foreground mt-2" data-testid="profile-stats"><b className="text-foreground">{p.followers}</b> followers · <b className="text-foreground">{p.following}</b> following · <b className="text-foreground">{p.posts}</b> looks · <b className="text-foreground">{p.listed}</b> listed</p>
        </div>
        {p.is_me ? (
          <Button variant="secondary" onClick={() => setEdit(true)} className="rounded-full" data-testid="profile-edit-button"><Pencil className="h-4 w-4 mr-1.5" /> Edit profile</Button>
        ) : (
          <Button variant={p.is_following ? "outline" : "default"} onClick={toggleFollow} className="rounded-full" data-testid="profile-follow-button">{p.is_following ? "Following" : "Follow"}</Button>
        )}
      </div>

      {p.is_me && <div className="mt-5 rounded-2xl border border-border bg-card p-5"><BadgeStrip /></div>}

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="looks" className="rounded-full" data-testid="profile-tab-looks"><Sparkles className="h-4 w-4 mr-1.5" /> Looks</TabsTrigger>
          <TabsTrigger value="shop" className="rounded-full" data-testid="profile-tab-shop"><Store className="h-4 w-4 mr-1.5" /> {p.is_me ? "For sale & trade" : "Shop & trade"}{p.listed > 0 && <span className="ml-1.5 text-xs text-muted-foreground">{p.listed}</span>}</TabsTrigger>
          {p.is_me && <TabsTrigger value="manage" className="rounded-full" data-testid="profile-tab-manage"><Tag className="h-4 w-4 mr-1.5" /> Manage listings</TabsTrigger>}
        </TabsList>

        <TabsContent value="looks" className="mt-5">
          {p.recent_posts?.length ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{p.recent_posts.map((post) => <PostCard key={post.id} post={post} onDelete={async (x) => { await api.deletePost(x.id); load(); }} />)}</div>
            : <p className="text-sm text-muted-foreground">{p.is_me ? <>No looks shared yet — render an outfit in the <Link className="underline" to="/planner">Planner</Link> and hit Share.</> : "No looks yet."}</p>}
        </TabsContent>

        <TabsContent value="shop" className="mt-5">
          {p.listed_items?.length ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <TradeHint>{p.is_me ? "This is what everyone else sees on your profile. Tap a piece to preview the offer flow." : `Make an offer or propose a swap — @${p.handle} is open to letting these go.`}</TradeHint>
                {p.is_me && <Button size="sm" variant="secondary" onClick={() => setTab("manage")} className="rounded-full" data-testid="profile-goto-manage">Edit listings</Button>}
              </div>
              <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="profile-listed-grid">{p.listed_items.map((it) => <MarketCard key={it.id} item={{ ...it, user: p }} />)}</div>
            </>
          ) : (
            <div className="rounded-[var(--radius)] border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="profile-listed-empty">
              {p.is_me ? <>Nothing listed yet. <button className="underline" onClick={() => setTab("manage")} data-testid="profile-empty-manage-link">Pick pieces to sell or trade</button>.</> : "Nothing listed right now."}
            </div>
          )}
        </TabsContent>

        {p.is_me && (
          <TabsContent value="manage" className="mt-5">
            <TradeHint>Flip pieces to “For sale”, “Open to swap” or both. Swappable pieces show fair trades (within ±20% of worth) from other closets.</TradeHint>
            <div className="mt-3"><ManageListings onChanged={load} /></div>
          </TabsContent>
        )}
      </Tabs>

      {p.is_me && <EditProfile open={edit} onOpenChange={setEdit} profile={p} onSaved={() => { load(); refresh(); }} />}
    </div>
  );
}

const EditProfile = ({ open, onOpenChange, profile, onSaved }) => {
  const [form, setForm] = useState({ handle: profile.handle, display_name: profile.display_name || "", bio: profile.bio || "", avatar_public: !!profile.avatar_public });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.updateMe(form); toast.success("Profile updated"); onOpenChange(false); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="profile-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit profile</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-muted-foreground">Handle</Label><Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="mt-1" data-testid="profile-handle-input" /></div>
          <div><Label className="text-xs text-muted-foreground">Display name</Label><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1" data-testid="profile-name-input" /></div>
          <div><Label className="text-xs text-muted-foreground">Bio</Label><Input value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="What's your style?" className="mt-1" data-testid="profile-bio-input" /></div>
          <div className="flex items-center justify-between rounded-xl bg-secondary/60 p-3">
            <div><p className="text-sm font-medium">Use my Twin face photo as avatar</p><p className="text-xs text-muted-foreground">Visible to everyone on the app</p></div>
            <Switch checked={form.avatar_public} onCheckedChange={(v) => setForm({ ...form, avatar_public: v })} data-testid="profile-avatar-switch" />
          </div>
          <Button onClick={save} disabled={busy} className="rounded-full w-full" data-testid="profile-save-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
